import { min, round } from 'lodash'
import moment from 'moment'

import { getSignOnUserName } from '@/modules/signOns/signOnHelper'
import {
  AttendanceOverride,
  AttendanceValue,
} from '@/modules/templates/types/blueprint'

import { SiteDiarySignOnsUiQuery } from 'types/api.graphql'

type SubcontractorAttendance =
  AttendanceValue['subcontractorAttendances'][number]
type WorkerAttendance = SubcontractorAttendance['workerAttendance'][number]
type SiteDiarySignOns = SiteDiarySignOnsUiQuery['siteDiary']['signOns']
type Time = string | moment.Moment

export const importWorkerAttendance = (
  blockValue: AttendanceValue,
  siteDiarySignOns: SiteDiarySignOns,
  projectId: string,
  includeDeletedEntries = false
): AttendanceValue => {
  let value: AttendanceValue = {
    subcontractorAttendances: [],
    visitors: [],
    overrides: [...(blockValue?.overrides || [])],
  }

  // step 1. convert all sign ons into worker attendances
  value = siteDiarySignOns.reduce((acc, signOn) => {
    // if visitor, add name to visitor list
    if (signOn.visitor) {
      acc.visitors.push(getSignOnUserName(signOn))
      return acc
    }

    // ignore any "deleted" auto attendances
    const deleted =
      value.overrides.find((o) => o.workerId === signOn.worker?.id)?.deleted &&
      !includeDeletedEntries
    if (deleted) return acc

    // find the "correct" subcontractor for this sign on
    const projectSubbie = signOn.worker?.subcontractors?.find((s) =>
      s.projects?.some((p) => p.id === projectId)
    )

    if (!projectSubbie) return acc

    const attendance = {
      workerId: signOn.worker?.id,
      hours:
        signOn.signOnHours?.hours ??
        (getWorkerHours(signOn) || min([7.6, getElapsedHours(signOn)])),
      signOnId: signOn.id,
      signedOnAt: getSignedOnAt(signOn),
      isManualSignedOnAt: !!signOn.manualSignedOnAt,
      signedOffAt: getSignedOffAt(signOn),
      isManualSignedOffAt: !!signOn.manualSignedOffAt,
    } as WorkerAttendance

    // find the current attendance for this subcontractor
    const existingSubbie = acc.subcontractorAttendances.find(
      (s) => s.subcontractorId === projectSubbie.id
    )

    const subbieComments = blockValue?.subcontractorAttendances?.find(
      (s) => s.subcontractorId === projectSubbie.id
    )?.comments

    // either add the attendance to the subbie, or create new subbie attendance
    if (existingSubbie) {
      existingSubbie.workerAttendance.push(attendance)
    } else {
      acc.subcontractorAttendances.push({
        subcontractorId: projectSubbie.id,
        workerAttendance: [attendance],
        manuallyCreated: false,
        comments: subbieComments,
      })
    }

    return acc
  }, value)

  // step 2. strip all non-manually-created-sign-ons due to legacy shenanigans
  const manualAttendances = getManuallyCreatedWorkerAttendances(blockValue)

  // step 3. add all manually-created-sign-ons to worker attendances
  manualAttendances.forEach((workerAttendance) => {
    const { subcontractorId, ...attendance } = workerAttendance

    // find the current attendance for this subcontractor
    const existingSubbie = value.subcontractorAttendances.find(
      (s) => s.subcontractorId === subcontractorId
    )

    const subbieComments = blockValue.subcontractorAttendances.find(
      (s) => s.subcontractorId === subcontractorId
    )?.comments

    // either add the attendance to the subbie, or create new subbie attendance
    if (existingSubbie) {
      existingSubbie.workerAttendance.push(attendance)
      existingSubbie.comments = subbieComments // add comments if they exist to auto imported attendance
    } else {
      value.subcontractorAttendances.push({
        subcontractorId,
        manuallyCreated: true,
        workerAttendance: [attendance],
        comments: subbieComments,
      })
    }
  })

  // step 4. add any missing subbies with comments
  const missingSubbiesWithComments = blockValue?.subcontractorAttendances
    ?.filter((s) => !!s.comments)
    ?.filter((s) => {
      return !value.subcontractorAttendances.some(
        (s2) => s2.subcontractorId === s.subcontractorId
      )
    })

  missingSubbiesWithComments?.forEach((s) => {
    value.subcontractorAttendances.push({
      subcontractorId: s.subcontractorId,
      comments: s.comments,
      workerAttendance: [],
      manuallyCreated: s.manuallyCreated,
    })
  })

  return value
}

export const diffHours = (start: Time, end: Time) => {
  const startTime = moment(start)
  const endTime = moment(end).set('seconds', startTime.seconds())
  const diffMins = endTime.diff(startTime, 'minutes')
  const roundedHours = round(diffMins / 60, 2)

  return roundedHours
}

const getSignedOnAt = (signOn: SiteDiarySignOns[number]): string => {
  return signOn.manualSignedOnAt || signOn.createdAt
}

const getSignedOffAt = (signOn: SiteDiarySignOns[number]): string => {
  return signOn.manualSignedOffAt || signOn.signedOffAt
}

const getElapsedHours = (signOn: SiteDiarySignOns[number]): number => {
  return diffHours(getSignedOnAt(signOn), moment())
}

const getWorkerHours = (signOn: SiteDiarySignOns[number]): number => {
  if (!signOn.signedOffAt && !signOn.manualSignedOffAt) return null

  return diffHours(getSignedOnAt(signOn), getSignedOffAt(signOn))
}
type ManuallyCreatedAttendance = WorkerAttendance & {
  subcontractorId: string
  adHoc: boolean
  subcontractorName?: string
}

const getManuallyCreatedWorkerAttendances = (
  value: AttendanceValue
): ManuallyCreatedAttendance[] => {
  const manualAttendances = [] as ManuallyCreatedAttendance[]

  value?.subcontractorAttendances?.forEach((subbie) => {
    subbie.workerAttendance?.forEach((attendance) => {
      if (attendance.manuallyCreated) {
        manualAttendances.push({
          ...attendance,
          subcontractorId: subbie.subcontractorId,
          adHoc: subbie.adHoc,
          subcontractorName: subbie.subcontractorName,
        })
      }
    })
  })

  return manualAttendances
}

export const calculateHours = (
  subbie: SubcontractorAttendance,
  overrides: AttendanceOverride[] = []
) => {
  const validAttendances = subbie.workerAttendance?.filter((a) => {
    if (a.manuallyCreated) {
      return true
    }

    return !overrides.some(({ workerId, deleted }) => {
      return workerId === a.workerId && deleted === true
    })
  })

  const hours =
    validAttendances?.reduce((agg, curr) => {
      if (isNaN(curr.hours)) return agg
      return (agg += curr.hours)
    }, 0) || 0

  return round(hours, 2)
}

export const calculateTotalHours = (
  value: SubcontractorAttendance[],
  overrides: AttendanceOverride[]
) => {
  const totalHours = value.reduce((agg, curr) => {
    return agg + calculateHours(curr, overrides)
  }, 0)

  return round(totalHours, 2)
}

export const cloneWorkerAttendances = (
  value: WorkerAttendance[]
): WorkerAttendance[] => {
  return value.map((attendance) => ({ ...attendance }))
}

export const cloneValue = (value: AttendanceValue): AttendanceValue => {
  return {
    overrides: [...(value.overrides || [])],
    visitors: value.visitors?.map((s) => s),
    subcontractorAttendances: value.subcontractorAttendances.map((subbie) => ({
      ...subbie,
      workerAttendance: subbie.workerAttendance.map((worker) => ({
        ...worker,
      })),
    })),
  }
}
